import { Module, VuexModule, MutationAction, getModule, Mutation } from 'vuex-module-decorators';
import store from '@/store';
import { UserProfile } from '@/models/dexie/UserProfile';
import { UserProfileService } from '@/services/index';
import { UnitsMenuItem } from '@/models/general/UnitsMenuItem';
import { ApiIdentifers } from '@/config/index';

export interface IUserProfileStoreState {
    userProfile: UserProfile;
    userProfileLoaded: boolean;
}

@Module({ dynamic: true, store, name: 'userProfileStore' })
class UserProfileStoreModule extends VuexModule {
    public userProfile: UserProfile = new UserProfile();
    public userProfileLoaded: boolean = false;
    /**
     * Fetch user profile by email
     *
     * Note:
     * 'userProfile' are replaced by returned object
     * whose shape must be `{userProfile: [...]}`
     *
     * @param userId    The email of the user
     */
    @MutationAction({ mutate: ['userProfile'] })
    public async FetchUserProfile(userId: string) {
        const userProfile = UserProfileStore.GetUserProfile;
        if (userProfile.userId && userProfile.userId === userId) {
            UserProfileStore.StripFinenessFromProfile();
            UserProfileStore.SetUserProfileLoaded();
            return { userProfile: UserProfileStore.GetUserProfile };
        } else {
            const response: UserProfile = await UserProfileService.FetchUserProfile(userId);
            UserProfileStore.SetUserProfileLoaded();
            // Strip after set via returned object — clean in mutation below
            if (response.units) {
                response.units = response.units.filter((u) => u.unitIdentifier !== ApiIdentifers.unitFineness);
            }
            return { userProfile: response };
        }
    }

    @Mutation
    public ChangeProfileUnit(inUnit: UnitsMenuItem) {
        // Fineness format was removed from user profile — do not store it
        if (inUnit.identifier === ApiIdentifers.unitFineness) {
            return;
        }
        const oldUnit = this.userProfile.units.find((unit) => unit.unitIdentifier === inUnit.identifier);
        if (oldUnit !== undefined) {
            oldUnit.selectedValue = inUnit.selectedUnitFormat;
        } else {
            this.userProfile.units.push({ unitIdentifier: inUnit.identifier, selectedValue: inUnit.selectedUnitFormat });
        }
        UserProfileService.PutUserProfile(this.userProfile);
    }

    /** Remove legacy Fineness entry if still present in cached profile */
    @Mutation
    public StripFinenessFromProfile() {
        if (this.userProfile.units) {
            this.userProfile.units = this.userProfile.units.filter((u) => u.unitIdentifier !== ApiIdentifers.unitFineness);
        }
    }

    @Mutation
    public SetUserProfileLoaded() {
        this.userProfileLoaded = true;
    }

    /**
     * Getter for userProfile
     */
    get GetUserProfile() {
        return this.userProfile;
    }

    /**
     * Getter for userProfileLoaded
     */
    get GetUserProfileLoaded() {
        return this.userProfileLoaded;
    }
}

export const UserProfileStore = getModule(UserProfileStoreModule);
